/* Surveys — list of available + completed surveys. Each one can be opened
 * inline in a small accordion-style panel and submitted in one click.
 * Kept intentionally simple: text-only questions, idempotent submit. */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Check, Clock, Send, ChevronDown } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

export default function Surveys() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/my/surveys');
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[900px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">My Surveys</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Quick check-ins help us improve your experience.</p>

        {loading ? (
          <div className="text-[#6E6E73] text-[14px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center">
            <ClipboardList className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
            <div className="text-[16px] font-bold text-[#1D1D1F]">No surveys assigned</div>
            <p className="text-[13.5px] text-[#6E6E73]">When ZHELTA needs your feedback, it'll show up here.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((s) => (
              <SurveyCard key={s.id} survey={s} api={api} onDone={reload} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function SurveyCard({ survey, api, onDone }) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const completed = !!survey.completed;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/api/academy/my/surveys/submit', {
        survey_id: survey.id,
        answers,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally { setBusy(false); }
  };

  return (
    <li data-testid={`survey-${survey.id}`}
      className={`rounded-2xl border bg-white overflow-hidden transition-shadow ${
        completed ? 'border-emerald-200' : 'border-[#E5E5E7] hover:shadow-[0_10px_30px_-15px_rgba(201,162,39,0.25)]'
      }`}>
      <button type="button"
        onClick={() => !completed && setOpen((v) => !v)}
        disabled={completed}
        data-testid={`survey-toggle-${survey.id}`}
        className="w-full flex items-start gap-3 px-5 py-4 text-left disabled:cursor-default">
        <span className={`inline-flex h-9 w-9 shrink-0 rounded-xl items-center justify-center ${
          completed ? 'bg-emerald-50 text-emerald-700' : 'bg-[#fff8e7] text-[#a8871a]'
        }`}>
          {completed ? <Check className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[15px] font-extrabold text-[#0e0e10]">{survey.title}</div>
            {completed ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                <Check className="w-3 h-3" /> Completed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="w-3 h-3" /> {survey.estimated_minutes ? `${survey.estimated_minutes} min` : 'Open'}
              </span>
            )}
          </div>
          {survey.subtitle && <div className="text-[12.5px] text-[#6E6E73] mt-0.5">{survey.subtitle}</div>}
        </div>
        {!completed && (
          <ChevronDown className={`w-4 h-4 text-[#6E6E73] mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {!completed && open && (
        <form onSubmit={submit} className="px-5 pb-5 pt-1 border-t border-[#E5E5E7] space-y-3">
          {(survey.questions || []).map((q) => (
            <label key={q.id} className="block">
              <span className="block text-[12px] font-bold text-[#0e0e10] mb-1">{q.label}</span>
              <textarea rows={2}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                data-testid={`survey-answer-${survey.id}-${q.id}`}
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227] resize-y" />
            </label>
          ))}
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-700">{error}</div>}
          <div className="pt-1 text-right">
            <button type="submit" disabled={busy}
              data-testid={`survey-submit-${survey.id}`}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              <Send className="w-3.5 h-3.5" /> {busy ? 'Submitting…' : 'Submit response'}
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
